import { CSSProperties, KeyboardEvent, ReactNode, RefCallback, RefObject } from 'react'

import { PICKER_TEXT } from '../constants'
import { DropdownMenu, MenuItemSelection } from '../Picker/DropdownMenu'
import { PickerOverlay } from '../Picker/PickerOverlay'
import { SearchBar, SearchChange } from '../Picker/SearchBar'
import { FocusItemValue } from '../Picker/useFocusItemValue'
import { PickerItem, PositionChildProps } from '../types'
import { getGroupedMenuRows, toMenuRows } from '../utils/getGroupedMenuRows'
import { mergeRefs } from '../utils/mergeRefs'
import { useClassNames } from '../utils/useClassNames'
import { PickerMenuSearch } from './CheckPicker.types'

interface CheckPickerMenuProps<Item extends PickerItem> {
  positionProps: PositionChildProps
  positionRef: RefCallback<HTMLElement>
  overlayRef: RefObject<HTMLDivElement>
  triggerRef: RefObject<HTMLElement>
  searchInputRef: RefObject<HTMLInputElement>
  data: Item[]
  stickyItems: Item[]
  selectedValues: Item['value'][]
  focusItemValue: FocusItemValue<Item>
  groupBy?: boolean
  searchable: boolean
  keepSearchFocused: boolean
  searchKeyword: string
  menuClassName?: string
  menuStyle?: CSSProperties
  isDisplayed: (check: { item: Item }) => boolean
  renderMenu?: (menu: ReactNode, search: PickerMenuSearch) => ReactNode
  renderMenuItem?: (label: ReactNode, item: Item) => ReactNode
  renderMenuGroup?: (title: ReactNode) => ReactNode
  onSearch: (change: SearchChange) => void
  onKeyDown: (event: KeyboardEvent<HTMLElement>) => void
  onSelect: (selection: MenuItemSelection<Item>) => void
}

export const CheckPickerMenu = <Item extends PickerItem>({
  positionProps,
  positionRef,
  overlayRef,
  triggerRef,
  searchInputRef,
  data,
  stickyItems,
  selectedValues,
  focusItemValue,
  groupBy,
  searchable,
  keepSearchFocused,
  searchKeyword,
  menuClassName,
  menuStyle,
  isDisplayed,
  renderMenu,
  renderMenuItem,
  renderMenuGroup,
  onSearch,
  onKeyDown,
  onSelect,
}: CheckPickerMenuProps<Item>) => {
  const { merge, prefix } = useClassNames('picker')

  const visibleStickyItems = stickyItems.filter(item => isDisplayed({ item }))
  const items = data.filter(
    item =>
      isDisplayed({ item }) && !stickyItems.some(stickyItem => stickyItem.value === item.value),
  )
  const rows = [
    ...toMenuRows(visibleStickyItems),
    ...(groupBy ? getGroupedMenuRows(items) : toMenuRows(items)),
  ]

  const overlayStyle = { ...menuStyle, left: positionProps.left, top: positionProps.top }

  const menu = rows.length ? (
    <DropdownMenu
      rows={rows}
      grouped={groupBy !== undefined}
      activeValues={selectedValues}
      focusItemValue={focusItemValue}
      renderMenuItem={renderMenuItem}
      renderMenuGroup={renderMenuGroup}
      onSelect={onSelect}
    />
  ) : (
    <div className={prefix('none')}>{PICKER_TEXT.noResults}</div>
  )

  return (
    <PickerOverlay
      ref={mergeRefs(overlayRef, positionRef)}
      className={merge(positionProps.className, menuClassName, prefix('check-menu'))}
      style={overlayStyle}
      triggerRef={triggerRef}
      onKeyDown={onKeyDown}
    >
      {searchable ? (
        <SearchBar
          placeholder={PICKER_TEXT.searchPlaceholder}
          value={searchKeyword}
          inputRef={searchInputRef}
          keepFocused={keepSearchFocused}
          onChange={onSearch}
        />
      ) : null}
      {renderMenu
        ? renderMenu(menu, {
            searchKeyword,
            clearSearch: event => onSearch({ keyword: '', event }),
          })
        : menu}
    </PickerOverlay>
  )
}
